import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import Swal from 'sweetalert2';

const AlakartRatingSummary = ({ planId, onClose, isDarkMode, onPlanUpdated }) => {
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState([]);
    const [error, setError] = useState(null);
    const [applyingId, setApplyingId] = useState(null);

    useEffect(() => {
        fetchSummary();
    }, [planId]);

    const fetchSummary = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.get(`${API_BASE_URL}/api/rating/summary/${planId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setSummary(res.data.summary);
            }
        } catch (err) {
            console.error('Fetch summary error:', err);
            setError('Sonuçlar alınamadı');
        } finally {
            setLoading(false);
        }
    };

    const handleApplyChoice = async (date, slot, dishId) => {
        setApplyingId(dishId);
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.post(`${API_BASE_URL}/api/rating/apply-choice`, {
                planId, date, slot, dishId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Onaylandı!',
                    text: 'Ailenin tercihi ana yemeğe dönüştürüldü.',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000
                });
                if (onPlanUpdated) onPlanUpdated();
                fetchSummary(); // Refresh summary UI
            }
        } catch (err) {
            Swal.fire('Hata', 'Değişiklik uygulanamadı', 'error');
        } finally {
            setApplyingId(null);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className={`relative ${isDarkMode ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'} p-8 rounded-[2.5rem] max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl font-sans`}
            >
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className="text-3xl font-black">🗳️ Hane Halkı Tercihleri</h2>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Ailenin favori seçimlerini buradan onaylayabilirsin.</p>
                    </div>
                    <button onClick={onClose} className="p-3 rounded-2xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-24">
                        <div className="animate-spin h-16 w-16 border-4 border-orange-500 border-t-transparent rounded-full"></div>
                    </div>
                ) : error ? (
                    <div className="text-center py-12 text-red-500 font-bold">{error}</div>
                ) : summary.length === 0 ? (
                    <div className="text-center py-24 bg-slate-50 dark:bg-slate-800/50 rounded-[2rem]">
                        <div className="text-6xl mb-4">⏳</div>
                        <h3 className="text-xl font-bold opacity-50">Henüz kimse oy kullanmadı.</h3>
                        <p className="text-sm opacity-50 px-12">Paylaştığın link üzerinden ailenin tercihlerini buraya düşecek.</p>
                    </div>
                ) : (
                    <div className="space-y-12">
                        {summary.map((item, index) => {
                            const dateLabel = new Date(item.date).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' });
                            return (
                                <div key={`${item.date}-${item.slot}`} className="space-y-4">
                                    <div className="flex items-center gap-4">
                                        <div className="h-px flex-1 bg-slate-100 dark:bg-slate-800"></div>
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{dateLabel} / {item.slot}</span>
                                        <div className="h-px flex-1 bg-slate-100 dark:bg-slate-800"></div>
                                    </div>

                                    <div className="grid gap-3">
                                        {[item.currentDish, ...item.alternatives].map((dish, dIdx) => {
                                            const isSelectedByFamily = Number(item.winningId) === Number(dish.id);
                                            const isCurrentMain = Number(item.currentDish.id) === Number(dish.id);
                                            const score = item.scores[dish.id] || 0;

                                            return (
                                                <div 
                                                    key={dish.id} 
                                                    className={`p-4 rounded-[1.5rem] border-2 transition-all flex items-center justify-between ${
                                                        isCurrentMain 
                                                        ? 'bg-blue-50/30 border-blue-100' 
                                                        : isSelectedByFamily 
                                                          ? 'bg-orange-50 border-orange-200 shadow-lg shadow-orange-100/50' 
                                                          : 'bg-white dark:bg-slate-800 border-transparent'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-4 flex-1">
                                                        <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-200 flex-shrink-0">
                                                            <img src={dish.image_url || dish.imageUrl || 'https://via.placeholder.com/150'} alt={dish.name} className="w-full h-full object-cover" />
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-0.5">
                                                                <h4 className="font-bold text-slate-900 dark:text-white leading-tight">{dish.name}</h4>
                                                                {isCurrentMain && <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">Mevcut</span>}
                                                                {isSelectedByFamily && !isCurrentMain && <span className="text-[10px] bg-orange-500 text-white px-2 py-0.5 rounded-full font-bold animate-pulse">Popüler 🔥</span>}
                                                            </div>
                                                            <div className="flex gap-1 flex-wrap">
                                                                {item.voteDetails.filter(v => String(v.dishId) === String(dish.id)).map((v, vIdx) => (
                                                                    <div key={vIdx} className="text-[9px] bg-slate-100 dark:bg-slate-700 font-bold px-1.5 py-0.5 rounded opacity-70">
                                                                        {v.member}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {!isCurrentMain && (
                                                        <button 
                                                            onClick={() => handleApplyChoice(item.date, item.slot, dish.id)}
                                                            disabled={applyingId !== null}
                                                            className="ml-4 px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-black shadow-lg hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
                                                        >
                                                            {applyingId === dish.id ? '...' : 'ŞEF ONAYI'}
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </motion.div>
        </div>
    );
};

export default AlakartRatingSummary;
